import { utcUnixMs } from "./date"

/**
 * A small in-process cache with a deadline per entry.
 *
 * Both callers of this are on a request path that runs in Lambda, so "in
 * process" means "for as long as this instance stays warm" — there is no
 * shared cache tier in the stack, and this deliberately does not add one. It
 * exists to stop a single page load from asking the database the same question
 * several times, and to let consecutive loads on a warm instance skip work
 * that has not changed.
 *
 * Entries hold the *promise*, not the resolved value, so concurrent callers
 * asking for the same key share one query rather than racing to issue their
 * own. A rejected promise is evicted immediately: a failed lookup must not be
 * what the next caller gets back.
 */
export type TtlCache<TValue> = {
  /** Resolves from cache when live, otherwise runs `load` and stores it. */
  resolve: (key: string, load: () => Promise<TValue>) => Promise<TValue>
  /** Reads without populating — for callers that can proceed without the value. */
  peek: (key: string) => Promise<TValue> | undefined
  /** Drops one key. Safe to call for a key that was never cached. */
  invalidate: (key: string) => void
  /** Drops every key whose name starts with `prefix`. */
  invalidatePrefix: (prefix: string) => void
  /** Drops everything. */
  clear: () => void
  /** Entry count, including expired entries not yet swept. For tests. */
  size: () => number
}

export type TtlCacheOptions = {
  /** How long an entry stays servable. */
  ttlMs: number
  /**
   * Ceiling on entries. Reaching it evicts in insertion order, which for a
   * TTL cache is close enough to oldest-first — every entry has the same
   * lifetime, so insertion order *is* expiry order.
   */
  maxEntries?: number
  /** Injectable clock, so tests do not have to sleep. */
  now?: () => number
}

type CacheEntry<TValue> = {
  value: Promise<TValue>
  expiresAt: number
}

export function createTtlCache<TValue>({
  ttlMs,
  maxEntries = 500,
  now = utcUnixMs,
}: TtlCacheOptions): TtlCache<TValue> {
  const entries = new Map<string, CacheEntry<TValue>>()

  function live(key: string) {
    const entry = entries.get(key)
    if (!entry) return undefined
    if (entry.expiresAt <= now()) {
      entries.delete(key)
      return undefined
    }
    return entry
  }

  function evictOldestIfFull() {
    if (entries.size < maxEntries) return
    const oldest = entries.keys().next()
    if (!oldest.done) entries.delete(oldest.value)
  }

  return {
    resolve(key, load) {
      const cached = live(key)
      if (cached) return cached.value

      const value = load().catch((error: unknown) => {
        // Evict by identity: a later write for the same key must survive a
        // rejection that arrives after it.
        if (entries.get(key)?.value === value) entries.delete(key)
        throw error
      })

      evictOldestIfFull()
      entries.set(key, { value, expiresAt: now() + ttlMs })
      return value
    },

    peek(key) {
      return live(key)?.value
    },

    invalidate(key) {
      entries.delete(key)
    },

    invalidatePrefix(prefix) {
      for (const key of entries.keys()) {
        if (key.startsWith(prefix)) entries.delete(key)
      }
    },

    clear() {
      entries.clear()
    },

    size() {
      return entries.size
    },
  }
}
